import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Search, MapPin, Star, ShieldCheck, BadgeCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/apiClient";

const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "renovation_globale", label: "Rénovation" },
  { id: "maconnerie", label: "Maçonnerie" },
  { id: "electricite", label: "Électricité" },
  { id: "plomberie", label: "Plomberie" },
  { id: "isolation_ite", label: "Isolation" },
  { id: "toiture", label: "Toiture" },
  { id: "peinture", label: "Peinture" },
];

export const ArtisansDirectory = () => {
  const [artisans, setArtisans] = useState([]);
  const [search, setSearch] = useState("");
  const [trade, setTrade] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/artisans", { params: { trade, search } });
      setArtisans(data);
    } catch (err) {
      console.error("Failed to load artisans:", err);
    }
  }, [trade, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="artisans-directory">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            data-testid="artisans-search-input"
            placeholder="Rechercher un artisan, une ville…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-full"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="artisans-filter-trade">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`artisans-filter-trade-${f.id}`}
            onClick={() => setTrade(f.id)}
            className={`text-sm px-4 py-1.5 rounded-full border transition-all ${
              trade === f.id
                ? "bg-[#0D6EFD] border-[#0D6EFD] text-white"
                : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {artisans.map((a, i) => (
          <div key={`${a.name}-${a.city}`} className="rounded-2xl bg-[#121A29] border border-white/10 p-5 hover:border-[#0D6EFD]/50 transition-colors idb-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-white font-semibold">{a.name}</h4>
                <p className="text-white/50 text-sm">{a.specialty}</p>
              </div>
              <div className="flex items-center gap-1 text-[#F59E0B] text-sm font-semibold">
                <Star className="w-4 h-4 fill-[#F59E0B]" /> {a.rating}
              </div>
            </div>
            <div className="flex items-center gap-1 text-white/50 text-sm mt-2">
              <MapPin className="w-3.5 h-3.5" /> {a.city} · {a.reviews} avis
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {a.labels?.map((l) => (
                <Badge key={l} className="bg-[#00C3A7]/15 text-[#00C3A7] border border-[#00C3A7]/30 hover:bg-[#00C3A7]/20 text-xs font-medium">
                  {l === "RGE" ? <BadgeCheck className="w-3 h-3 mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                  {l}
                </Badge>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="text-white/40 text-xs">{a.budget}</span>
              <Button
                size="sm"
                data-testid="artisans-card-contact-btn"
                onClick={() => toast.success(`Demande envoyée à ${a.name}. L'artisan vous recontacte sous 48h.`)}
                className="bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full text-xs h-8"
              >
                Voir le profil
              </Button>
            </div>
          </div>
        ))}
        {artisans.length === 0 && (
          <p className="col-span-full text-center text-white/40 py-8">Aucun artisan trouvé pour cette recherche.</p>
        )}
      </div>
    </div>
  );
};
